import React from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Stack,
  Divider,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Send as SendIcon,
  Assignment as AssignmentIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Message as MessageIcon,
  Assessment as AssessmentIcon,
} from "@mui/icons-material";

const steps = [
  {
    label: "Set Up Weekly Questions",
    description: "Go to the Admin Page and configure custom report questions that your clients will answer in their weekly check-ins.",
    Icon: SettingsIcon,
  },
  {
    label: "Client Sends Check-In",
    description: "Your client clicks the 'Send Check-In' button in the mobile app to start their weekly report.",
    Icon: SendIcon,
  },
  {
    label: "Client Completes Questionnaire",
    description: "The client fills out the questionnaire with answers to your weekly questions and submits the report.",
    Icon: AssignmentIcon,
  },
  {
    label: "Report Appears in Unprocessed Reports",
    description: "Once submitted, the report automatically appears in your Unprocessed Reports list, ready for review.",
    Icon: VisibilityIcon,
  },
  {
    label: "Coach Reviews Report Data",
    description: "Click on the report to review all data collected for that time period, including answers to your questions and app activity summaries.",
    Icon: AssessmentIcon,
  },
  {
    label: "Coach Processes Report",
    description: "After reviewing the data, process the report to mark it as complete and move it to your processed reports history.",
    Icon: CheckCircleIcon,
  },
  {
    label: "Coach Provides Feedback",
    description: "Send your client a message with feedback, encouragement, or adjustments based on their report data.",
    Icon: MessageIcon,
  },
];

const ReportOnboardingFlow: React.FC = () => {
  return (
    <Box sx={{ maxWidth: 800, mx: "auto", p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold", mb: 3 }}>
        How the Weekly Report Process Works
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Weekly reports help you track your clients' progress and gather valuable insights. Here's how the process flows from setup to feedback.
      </Typography>

      <Stepper orientation="vertical" sx={{ mt: 2 }}>
        {steps.map((step, index) => {
          const IconComponent = step.Icon;
          return (
            <Step key={step.label} active={true} completed={false}>
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      backgroundColor: "primary.main",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: 2,
                    }}
                  >
                    <IconComponent />
                  </Box>
                )}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {step.label}
                </Typography>
              </StepLabel>
              <StepContent>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 3,
                    backgroundColor: "grey.50",
                    borderLeft: "4px solid",
                    borderColor: "primary.main",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Paper>
              </StepContent>
            </Step>
          );
        })}
      </Stepper>

      <Divider sx={{ my: 4 }} />

      <Paper
        elevation={2}
        sx={{
          p: 3,
          backgroundColor: "primary.light",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <AssessmentIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              What's Included in Reports
            </Typography>
          </Box>
        </Stack>
        <Typography variant="body2" sx={{ opacity: 0.95 }}>
          Each weekly report contains a comprehensive summary of all data collected in the mobile app during that time period, including:
        </Typography>
        <Stack spacing={1} sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Box component="span" sx={{ mt: 0.5 }}>•</Box>
            <Typography variant="body2">
              Answers to your custom weekly questions
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Box component="span" sx={{ mt: 0.5 }}>•</Box>
            <Typography variant="body2">
              Workout completion and exercise data
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Box component="span" sx={{ mt: 0.5 }}>•</Box>
            <Typography variant="body2">
              Nutrition and meal plan adherence
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Box component="span" sx={{ mt: 0.5 }}>•</Box>
            <Typography variant="body2">
              Progress photos and measurements
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Box component="span" sx={{ mt: 0.5 }}>•</Box>
            <Typography variant="body2">
              Overall activity and engagement metrics
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default ReportOnboardingFlow;












